import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextAWS';
import leagueApiService from '../services/leagueApi';

function Dashboard() {
  const { user } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      loadLeagues();
      loadPendingInvites();
    }
  }, [user]);

  const loadPendingInvites = async () => {
    try {
      if (!user?.email) { setPendingInvites([]); return; }

      const apiService = await import('../services/apiGraphQL.js').then(m => m.default);
      if (!apiService) { setPendingInvites([]); return; }

      const allLeaguesResult = await apiService.getAllLeagues();

      if (allLeaguesResult.success && allLeaguesResult.leagues) {
        const invites = allLeaguesResult.leagues.map(league => {
          const teams = typeof league.teams === 'string' ? JSON.parse(league.teams) : league.teams || [];
          const invitedTeam = teams.find(t => t.status === 'invited' && t.ownerEmail === user.email);

          if (invitedTeam) {
            const settings = typeof league.settings === 'string' ? JSON.parse(league.settings) : league.settings;
            const season = typeof league.season === 'string' ? JSON.parse(league.season) : league.season;
            return {
              id: league.leagueId,
              leagueId: league.leagueId,
              name: league.name,
              description: league.description,
              commissioner: teams.find(t => t.owner === league.creatorId)?.ownerName || 'Commissioner',
              maxTeams: settings?.maxTeams || 12,
              scoringType: settings?.scoringType || 'Standard',
              seasonYear: season?.year || 2026,
              draftDate: league.draftDate,
              status: league.status || 'Draft',
              teams,
              invitedTeam,
              inviteCode: invitedTeam.inviteCode,
            };
          }
          return null;
        }).filter(Boolean);

        setPendingInvites(invites);
      }
    } catch (err) {
      setPendingInvites([]);
    }
  };

  const loadLeagues = async () => {
    try {
      setLoading(true);
      setError('');

      if (!user) { setLoading(false); return; }

      const userId = user.userId || user.id || user.sub;
      if (!userId) {
        setError('Please log in to view your leagues.');
        setLeagues([]);
        setLoading(false);
        return;
      }

      const result = await leagueApiService.getLeagues(userId);

      if (result.success && result.leagues) {
        const transformedLeagues = result.leagues.map(league => {
          const settings = typeof league.settings === 'string' ? JSON.parse(league.settings) : league.settings;
          const season = typeof league.season === 'string' ? JSON.parse(league.season) : league.season;
          const teams = typeof league.teams === 'string' ? JSON.parse(league.teams) : league.teams || [];

          return {
            id: league.leagueId,
            leagueId: league.leagueId,
            name: league.name,
            description: league.description,
            commissioner: teams.find(t => t.owner === league.creatorId)?.ownerName || 'Commissioner',
            maxTeams: settings?.maxTeams || 12,
            scoringType: settings?.scoringType || 'Standard',
            season,
            seasonYear: season?.year || 2026,
            seasonStartDate: season?.startDate,
            seasonEndDate: season?.endDate,
            draftDate: league.draftDate,
            draftTime: league.draftTime,
            isPrivate: league.isPrivate,
            status: league.status || 'Draft',
            teams,
            createdAt: league.createdAt,
            updatedAt: league.updatedAt,
          };
        });

        setLeagues(transformedLeagues);
      } else {
        setLeagues([]);
      }
    } catch (err) {
      if (err.message?.includes('Backend API not available')) {
        setError('Backend is not fully deployed yet. Please wait a moment and try again.');
      } else if (err.message?.includes('not authorized')) {
        setError('Please log in to view your leagues.');
      } else {
        setError(`Failed to load leagues: ${err.message || 'Unknown error'}`);
      }
      setLeagues([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (invite) => {
    try {
      const userId = user.userId || user.id || user.sub;
      const userEmail = user.email;

      const leagueResult = await leagueApiService.getLeague(invite.leagueId);
      if (!leagueResult.success) throw new Error('Failed to fetch league data');

      let teams = leagueResult.league.teams || [];
      if (typeof teams === 'string') teams = JSON.parse(teams);

      if (teams.some(t => t.owner === userId && t.status !== 'invited')) {
        alert('You are already a member of this league!');
        return;
      }

      const invitedTeam = teams.find(t => t.ownerEmail === userEmail && t.status === 'invited');
      if (invitedTeam) {
        const userFullName = user.fullName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.username;
        invitedTeam.owner = userId;
        invitedTeam.ownerName = userFullName;
        invitedTeam.status = 'active';
        delete invitedTeam.invitedAt;
        delete invitedTeam.inviteCode;

        await leagueApiService.updateLeague(invite.leagueId, { teams });
        alert(`Successfully joined "${invite.name}"!`);
        await loadLeagues();
        await loadPendingInvites();
      } else {
        throw new Error('Invitation not found or already accepted');
      }
    } catch (err) {
      alert(`Failed to accept invitation: ${err.message}`);
    }
  };

  const handleDeclineInvite = async (invite) => {
    if (window.confirm(`Decline the invitation to "${invite.name}"?`)) {
      try {
        const userEmail = user.email;
        const leagueResult = await leagueApiService.getLeague(invite.leagueId);
        if (!leagueResult.success) throw new Error('Failed to fetch league data');

        let teams = leagueResult.league.teams || [];
        if (typeof teams === 'string') teams = JSON.parse(teams);

        const updatedTeams = teams.map(t =>
          t.ownerEmail === userEmail && t.status === 'invited' ? { ...t, status: 'declined' } : t
        );

        await leagueApiService.updateLeague(invite.leagueId, { teams: updatedTeams });
        await loadPendingInvites();
      } catch (err) {
        alert(`Failed to decline invitation: ${err.message}`);
      }
    }
  };

  const handleLeaveLeague = async (leagueId) => {
    if (window.confirm('Are you sure you want to leave this league?')) {
      try {
        const userId = user.userId || user.id || user.sub;
        await leagueApiService.leaveLeague(leagueId, userId);
        await loadLeagues();
      } catch (err) {
        alert(err.message || 'Failed to leave league');
      }
    }
  };

  const statusChipClass = (status) => {
    if (status === 'Active') return 'chip chip-active';
    if (status === 'Draft') return 'chip chip-draft';
    return 'chip chip-paused';
  };

  return (
    <div className="page-content">
      {/* Page Header */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-header">
          <div>
            <h1 className="text-title" style={{ marginBottom: '4px' }}>My Leagues</h1>
            <p className="text-secondary">Manage your fantasy baseball leagues and rosters.</p>
          </div>
          <div className="card-actions">
            <Link to="/create-league" className="btn btn-primary btn-sm">Create League</Link>
            <Link to="/join-league" className="btn btn-secondary btn-sm">Join League</Link>
          </div>
        </div>
      </div>

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <div className="card" style={{ marginBottom: '16px', borderColor: 'var(--primary)', borderWidth: '1.5px' }}>
          <h2 className="text-card-title" style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            Pending Invitations
            <span className="count-bubble">{pendingInvites.length}</span>
          </h2>
          <p className="text-secondary" style={{ marginBottom: '16px' }}>
            You've been invited to join these leagues.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {pendingInvites.map(invite => (
              <div key={invite.id} className="invite-card">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '28px' }}>{invite.invitedTeam.logoEmoji || '⚾'}</span>
                    <div>
                      <h3 className="text-player-name" style={{ margin: 0 }}>
                        {invite.name}
                        <span className="badge badge-primary" style={{ marginLeft: '8px' }}>
                          {invite.seasonYear}
                        </span>
                      </h3>
                      <p className="text-secondary" style={{ margin: '3px 0 0' }}>
                        Playing as: <strong style={{ color: 'var(--foreground)' }}>{invite.invitedTeam.name}</strong>
                      </p>
                    </div>
                  </div>

                  <div className="invite-card-meta">
                    <div><strong style={{ color: 'var(--foreground)' }}>Commissioner:</strong> {invite.commissioner}</div>
                    <div><strong style={{ color: 'var(--foreground)' }}>Teams:</strong> {invite.teams.length}/{invite.maxTeams}</div>
                    <div><strong style={{ color: 'var(--foreground)' }}>Scoring:</strong> {invite.scoringType}</div>
                    {invite.draftDate && (
                      <div><strong style={{ color: 'var(--foreground)' }}>Draft:</strong> {new Date(invite.draftDate).toLocaleDateString()}</div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => handleAcceptInvite(invite)}>Accept</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleDeclineInvite(invite)}>Decline</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '16px' }}>
          <span>✕</span>
          <div style={{ flex: 1 }}>
            <p style={{ margin: '0 0 8px' }}>{error}</p>
            <button onClick={loadLeagues} className="btn btn-primary btn-sm">Retry</button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="empty-state">
          <div className="spinner" style={{ margin: '0 auto 12px' }} />
          <p className="text-secondary">Loading your leagues…</p>
        </div>
      ) : leagues.length === 0 && !error ? (
        /* Empty State */
        <div className="empty-state">
          <div className="empty-state-icon">⚾</div>
          <h3 className="empty-state-title">No leagues yet</h3>
          <p className="empty-state-desc" style={{ marginBottom: '24px' }}>
            Create a new league or join an existing one to get started.
          </p>
          <div className="card-actions" style={{ justifyContent: 'center' }}>
            <Link to="/create-league" className="btn btn-primary">Create League</Link>
            <Link to="/join-league" className="btn btn-secondary">Join League</Link>
          </div>
        </div>
      ) : (
        /* League Grid */
        <div className="league-list">
          {leagues.map(league => {
            const leagueSeason = league.season || {
              startDate: league.seasonStartDate || '2026-03-26',
              endDate: league.seasonEndDate || '2026-09-27',
              year: league.seasonYear || 2026,
            };

            return (
              <div key={league.id} className="league-card">
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                      <h3 className="text-player-name" style={{ margin: 0 }}>{league.name}</h3>
                      <span className="badge badge-primary">{leagueSeason.year}</span>
                    </div>

                    <div className="league-info">
                      <p><strong>Commissioner:</strong> {league.commissioner}</p>
                      <p><strong>Teams:</strong> {league.teams.length}/{league.maxTeams}</p>
                      <p><strong>Scoring:</strong> {league.scoringType}</p>
                      {league.draftDate && <p><strong>Draft:</strong> {league.draftDate}</p>}
                      <p>
                        <strong>Status:</strong>{' '}
                        <span className={statusChipClass(league.status)}>{league.status}</span>
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
                    <Link to={`/league/${league.id}/roster`} className="btn btn-primary btn-sm">Open</Link>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleLeaveLeague(league.id)}>Leave</button>
                  </div>
                </div>

                {/* Mini standings */}
                {league.teams.length > 0 && (
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                    <p className="mini-standings-label">Standings</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {league.teams.slice(0, 5).map((team, index) => (
                        <div
                          key={team.id || index}
                          className={`mini-standings-row${index < 3 ? ' highlighted' : ''}`}
                        >
                          <span className={index < 3 ? undefined : 'text-secondary'}>
                            <span className="text-secondary" style={{ marginRight: '6px', fontSize: '12px' }}>{index + 1}.</span>
                            {team.name}
                          </span>
                          <span style={{ fontWeight: '600' }}>{team.points || 0} pts</span>
                        </div>
                      ))}
                      {league.teams.length > 5 && (
                        <p className="text-secondary" style={{ textAlign: 'center', marginTop: '4px', fontSize: '13px' }}>
                          +{league.teams.length - 5} more teams
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
